import React, { useState, useMemo } from 'react';
import T from 'prop-types';
import { mapValues, isEmpty } from 'lodash-es';

import ChartLegend from '../../ChartLegend';
import ChartTypeToggle from '../../ChartTypeToggle';
import { MarketShareDropdown, marketShareMetrics } from '../../MetricLists';
import Skeleton from '../../../../../components/Skeleton';
import { SmartChart } from '../../../../../components/Chart';
import useChartLegend from '../../../hooks/useChartLegend';
import { marketShareConfig, colors } from './config';
import { basePartialFilters } from '../../../configs/chart-config';

const emptyObject = {};

const MarketShareChart = ({ apps, series, isPending }) => {
  const [selectedMetric, setSelectedMetric] = useState(Object.keys(marketShareMetrics)[0]);
  const [chartType, setChartType] = useState('column');
  const { activeItem, hoveredItem, onClick, onMouseOver, onMouseOut, classesToAdd } =
    useChartLegend();

  const metricSeries = series?.[selectedMetric] || emptyObject;

  const sortedMetric = useMemo(() => {
    if (isEmpty(metricSeries)) return emptyObject;

    return apps.reduce(
      (acc, app) => ({
        ...acc,
        [app.unified_ref_no]: metricSeries[app.unified_ref_no] || [],
      }),
      {}
    );
  }, [apps, metricSeries]);

  const appByRefNo = apps.reduce(
    (acc, app, index) => ({
      ...acc,
      [app.unified_ref_no]: { ...app, color: colors[index] },
    }),
    {}
  );

  const metaSeriesData = mapValues(sortedMetric, (_, appRefNo) => ({
    type: chartType,
    color: appByRefNo[appRefNo]?.color,
    name: `${appByRefNo[appRefNo]?.nickname || 'Unknown'}`,
    id: appRefNo,
    visible: activeItem?.id && activeItem?.id === appRefNo,
    className: classesToAdd(appRefNo),
  }));

  const chartConfig = marketShareConfig({
    chartType,
    activeItem,
    hoveredItem,
    onClick,
    onMouseOver,
    onMouseOut,
  });

  return (
    <div className="market-share-chart">
      <header className="market-share-chart__header">
        <MarketShareDropdown selected={selectedMetric} onChange={setSelectedMetric} />
        <ChartTypeToggle active={chartType} disabled={isPending} onChange={setChartType} />
      </header>
      {isPending ? (
        <Skeleton height={340} className="d-flex" />
      ) : (
        <div className="chart-container">
          <div onMouseOut={onMouseOut} onBlur={onMouseOut}>
            <SmartChart
              chartId="market-share-chart"
              series={sortedMetric}
              metaSeriesData={metaSeriesData}
              config={chartConfig}
              allowPartialPeriods
              filters={basePartialFilters}
            />
          </div>
          <ChartLegend
            series={Object.values(metaSeriesData)}
            activeItem={activeItem?.id || hoveredItem?.id}
            onClick={onClick}
            onMouseOver={onMouseOver}
            onMouseOut={onMouseOut}
          />
        </div>
      )}
    </div>
  );
};

MarketShareChart.propTypes = {
  series: T.object,
  apps: T.array,
  isPending: T.bool.isRequired,
};

export default MarketShareChart;
